import React from "react";
import { Box, Typography, IconButton, Link, GlobalStyles } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmber from "@mui/icons-material/WarningAmber";
import EpAlert from "../../assets/images/category_icons/ep-alert.svg";
import EpCaution from "../../assets/images/category_icons/ep-caution.svg";
import EpInfo from "../../assets/images/category_icons/ep-info.svg";
import EpSuccess from "../../assets/images/category_icons/ep-success.svg";


const typeConfig = {
  success: {
    color: "#d6fbd8",       // accent bar color
    bg: "#e8f5e9",           // accent bar background
    iconColor: "#6ffb76",    // match icon (or the fill inside SVG)
    icon: <Box component="img" src={EpSuccess} alt="Success" width={24} height={24} />,
  },
  error: {
    color: "#f9b6b6",
    bg: "#ffffff",
    iconColor: "#bc3030",
    icon: <Box component="img" src={EpAlert} alt="Error" width={24} height={24} />,
  },
  warning: {
    color: "#fee3cd",
    bg: "#fff4e5",
    iconColor: "#edc16b",
    icon: <Box component="img" src={EpCaution} alt="Warning" width={24} height={24} />,
  },
  /* Password expiration: icon strip #FFEEB6, icon #EBB708 (see left column + WarningAmber). */
  password_expiration: {
    color: "#FFEEB6",
    bg: "#fff",
    iconColor: "#EBB708",
    icon: <Box component="img" src={EpCaution} alt="Warning" width={24} height={24} />,
  },
  info: {
    color: "#caeafb",
    bg: "#e1f5fe",
    iconColor: "#03a9f4",
    icon: <Box component="img" src={EpInfo} alt="Info" width={24} height={24} />,
  },
};

const passwordExpirationActionLinkSx = (() => {
  const base = {
    color: "#3F51B5 !important",
    textDecorationColor: "#3F51B5",
  };
  return {
    ...base,
    "&:hover, &:visited, &:active, &:focus": { ...base },
  };
})();

const Alert = ({ message, onClose, isTopToast }) => {
  const config = typeConfig[message?.type] || typeConfig.error;
  const rawTitle =
    message?.title ||
    (message?.type === "success" ? "Success" : "Unable to complete action") ||
    message?.text;
  // Never show generic "Notification"; use session-expired title when text suggests timeout.
  const title =
    rawTitle === "Notification" && /session expired|sign in again/i.test(message?.text || "")
      ? "Session expired"
      : rawTitle === "Notification"
        ? "Unable to complete action"
        : rawTitle;

  return (
    <Box
      sx={{
        display: "flex",
        width: "auto",
        maxWidth: 480,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 3,
        overflow: "hidden",
        position: "relative", // make container relative for absolute progress bar
      }}
    >
      {/* Left Accent Section */}
      <Box
        sx={{
          width: 40,
          bgcolor: config.color, // vertical accent bar
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {message?.type === "password_expiration" ? (
          <WarningAmber sx={{ color: "#EBB708", fontSize: 24 }} />
        ) : (
          <Box
            component="img"
            src={config.icon.props.src}
            alt={config.icon.props.alt}
            width={24}
            height={24}
          />
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          position: "relative",
          minWidth: 0,
          pr: 5,
        }}
      >
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            fontWeight={600}
            fontSize={14}
            lineHeight={1.35}
            sx={{ whiteSpace: "normal" }}
          >
            {title}
          </Typography>

          {message?.text && (
            <Typography fontSize={13} color="text.secondary" mt={0.5}>
              {message.text}
            </Typography>
          )}
        </Box>

        {message?.actionLink && (
          <Link
            component="button"
            underline="always"
            fontSize={13}
            mt={1}
            onClick={message.actionLink.onClick}
            sx={
              message?.type === "password_expiration"
                ? passwordExpirationActionLinkSx
                : undefined
            }
          >
            {message.actionLink.label}
          </Link>
        )}
      </Box>

      {message?.autoHideDuration && (
        <>
          <GlobalStyles
            styles={{
              "@keyframes toast-progress": {
                from: { width: "0%" },
                to: { width: "100%" },
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: 4,
              overflow: "hidden",
            }}
          >
            {/* Track: same hue as icon, light */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: config.iconColor,
                opacity: 0.25,
              }}
            />
            {/* Fill: icon color, animates left to right */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: 0,
                maxWidth: "100%",
                bgcolor: config.iconColor,
                animation: `toast-progress ${message.autoHideDuration}ms linear forwards`,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Alert;